import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { Room } from "./RoomService";
import { EventTeam, EventTeamInput } from "./EventTeamService";
import { Client } from "./ClientService";
import { RecurringEvent } from "./RecurringEventService";
import { Venue } from "./VenueService";
import { User } from "./UserService";
import { Category } from "./CategoryService";

// GraphQL queries
const EVENT_LIST_QUERY = gql`
  query EventList($filter: EventFilter, $params: PaginationAndSorting) {
    eventList(filter: $filter, params: $params) {
      code
      data {
        id
        venueId
        recurringEventId
        clientId
        roomId
        name
        description
        eventDate
        startTime
        endTime
        color
        eventUid
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        room {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          name
          roomTypeId
        }
        eventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_GET_QUERY = gql`
  query EventGet($id: ID) {
    eventGet(id: $id) {
      code
      data {
        id
        venueId
        recurringEventId
        clientId
        roomId
        name
        description
        eventDate
        startTime
        endTime
        color
        eventUid
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        room {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          name
          roomTypeId
        }
        eventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_GET_MANY_QUERY = gql`
  query EventGetMany($ids: [ID]) {
    eventGetMany(ids: $ids) {
      code
      data {
        id
        venueId
        recurringEventId
        clientId
        roomId
        name
        description
        eventDate
        startTime
        endTime
        color
        eventUid
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        room {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          name
          roomTypeId
        }
        eventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EVENT_CREATE_MUTATION = gql`
  mutation EventCreate($params: EventInput) {
    eventCreate(params: $params) {
      code
      data {
        id
        venueId
        recurringEventId
        clientId
        roomId
        name
        description
        eventDate
        startTime
        endTime
        color
        eventUid
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        room {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          name
          roomTypeId
        }
        eventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_UPDATE_MUTATION = gql`
  mutation EventUpdate($where: EventWhereInput, $params: EventInput) {
    eventUpdate(where: $where, params: $params) {
      code
      data {
        id
        venueId
        recurringEventId
        clientId
        roomId
        name
        description
        eventDate
        startTime
        endTime
        color
        eventUid
        categories {
          id
          name
        }
        imageUploadedFileId
        imageUrl
        externalUrlType
        externalUrl
        status
        createdBy
        updatedBy
        createdAt
        updatedAt
        room {
          id
          roomTypeId
          name
          roomNumber
          description
          capacity
          footageSqm
          floor
          locationNotes
        }
        rooms {
          id
          name
          roomTypeId
        }
        eventTeams {
          id
          teamName
          supportingRooms {
            id
            roomTypeId
            name
            roomNumber
            description
            capacity
            footageSqm
            floor
            locationNotes
          }
        }
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_IMPORT_MUTATION = gql`
  mutation EventImport($params: EventImportInput) {
    eventImport(params: $params) {
      code
      data {
        inserted
        updated
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_REMOVE_MUTATION = gql`
  mutation EventRemove($where: EventWhereInput) {
    eventRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EVENT_REMOVE_MANY_MUTATION = gql`
  mutation EventRemoveMany($where: [EventWhereInput]) {
    eventRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface Event {
  id: string;
  venueId?: string;
  recurringEventId?: string;
  clientId?: string;
  roomId?: string;
  name?: string;
  description?: string;
  eventDate?: string;
  startTime?: string;
  endTime?: string;
  color?: string;
  eventUid?: string;
  recurrencePattern?: string | null;
  daysOfWeek?: string | null;
  displayZoneId?: string | null | undefined;
  categories?: Category[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  imageUrl?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status?: number;
  createdBy?: string;
  updatedBy?: string;
  createdAt?: string;
  updatedAt?: string;
  userCreated?: User;
  userUpdated?: User;
  venue?: Venue;
  recurringEvent?: RecurringEvent;
  client?: Client;
  room?: Room;
  eventTeams?: EventTeam[];
  rooms?: Room[];
}

export interface EventFilter {
  id?: string[];
  venueId?: string[];
  recurringEventId?: string[];
  clientId?: string[];
  roomId?: string;
  name?: string;
  eventUid?: string[];
  eventDate?: string;
  eventDateStart?: string;
  eventDateEnd?: string;
  status?: number[];
}

export interface EventInput {
  venueId?: string | null | undefined;
  recurringEventId?: string | null | undefined;
  clientId?: string | null | undefined;
  roomId?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  eventDate?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  color?: string | null | undefined;
  eventUid?: string | null | undefined;
  categories?: string[] | null | undefined;
  imageUploadedFileId?: string | null | undefined;
  externalUrlType?: string | null | undefined;
  externalUrl?: string | null | undefined;
  status?: number | null | undefined;
  eventTeams?: EventTeamInput[] | null | undefined;
  rooms?: string[] | null | undefined; // assigned rooms IDs
}

export interface ImportedEventInput {
  eventUid?: string | null | undefined;
  name?: string | null | undefined;
  description?: string | null | undefined;
  eventDate?: string | null | undefined;
  startTime?: string | null | undefined;
  endTime?: string | null | undefined;
  organizer?: string | null | undefined;
  status?: string | null | undefined;
}

export interface EventImportInput {
  mode?: string;
  venueId?: string;
  events?: ImportedEventInput[];
}

export interface EventImportStats {
  inserted: number;
  updated: number;
}

export interface EventWhereInput {
  id: string;
}

/**
 * Event service that handles all event-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, import, remove, removeMany
 */
export class EventService {
  /**
   * Get a list of events with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EventFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_LIST_QUERY, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single event by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_GET_QUERY, {
      id: id,
    });
    return result;
  }

  /**
   * Get multiple events by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(EVENT_GET_MANY_QUERY, {
      ids: ids,
    });
    return result;
  }

  /**
   * Create a new event
   */
  static async create(params: EventInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EVENT_CREATE_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Update an existing event
   */
  static async update(id: string, params: EventInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EVENT_UPDATE_MUTATION, {
      where: { id },
      params,
    });
    return result;
  }

  /**
   * Import events from external source
   */
  static async import(params: EventImportInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(EVENT_IMPORT_MUTATION, {
      params,
    });
    return result;
  }

  /**
   * Remove/delete an event
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(EVENT_REMOVE_MUTATION, {
      where: { id },
    });
    return result;
  }

  /**
   * Remove/delete multiple events
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EVENT_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
